import React from "react";

import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";

import Numerical from "@busbud/design-system-components/dist/Numerical";
import Stack from "@busbud/design-system-components/dist/Stack";
import Text from "@busbud/design-system-components/dist/Text";

import type { ErrorModalButtonType } from "@app/helpers/api-error";
import { useAppContext } from "@app/helpers/hooks";
import { getCartV2TripItem } from "@app/modules/checkout/utils/cart-v2";
import { useNavigation } from "@app/modules/results/hooks/use-navigation";
import { getDisplayedPriceConfig } from "@app/modules/results/selectors/get-displayed-price-config";
import type { NapiV2Cart } from "@app/types/napi-v2-cart-types";
import type { ResultsReduxState } from "@app/types/results-redux-types";

import type { Dispatch } from "@reduxjs/toolkit";
import type { ConnectedProps } from "react-redux";

import { IncludedFees, shouldIncludeFees } from "../../../helpers/price";
import { MoneyCeiled } from "../../../lib/money";
import {
  addToCartAcceptNewPrice,
  addToCartRefuseNewPrice
} from "../../../modules/results/actions";
import { getTripDisplayPrice } from "../../../modules/results/selectors/trips/get-trip-display-price";
import { getSearchedLegOperators } from "../../../modules/results/selectors/trips/get-trip-operators";
import { getComparableCartPrice } from "../../../modules/results/utils";
import { AlertDialog } from "../../alert-dialog";
import { getRedirectUrl } from "../utils/reload-outbound-page";

interface OwnProps {
  id: string;
  cart: NapiV2Cart;
  onAccept?: () => void;
  is_async_cart?: boolean;
}

const PriceChangedComponent: React.FC<ConnectorProps> = props => {
  const {
    addToCartAcceptNewPrice,
    addToCartRefuseNewPrice,
    cart,
    currency,
    displayed_price_config,
    id,
    operator_name,
    price_selected,
    search,
    onAccept,
    is_async_cart = false
  } = props;
  const { browser_locale, translator, urlBuilder } = useAppContext();
  const { utm_campaign } = useNavigation();

  const is_google_bus = utm_campaign === "google_bus";

  const getFormattedPrice = (price_in_cents: number) => {
    return new MoneyCeiled(price_in_cents).getPriceWithoutCents(
      currency,
      browser_locale
    );
  };

  const price_updated = getComparableCartPrice(cart.charges, {
    include_fees: is_google_bus
      ? IncludedFees.ALL
      : shouldIncludeFees({
          ...displayed_price_config,
          sources:
            getCartV2TripItem(cart.items)?.segments.map(
              segment => segment.source_name
            ) || []
        })
  });

  const message = (
    <Stack direction="column" alignItems="center" spacing={2}>
      <Text size="lg" align="center">
        {translator.t("!results.modal.confirm_price.message.new_price", {
          old_price: getFormattedPrice(price_selected)
        })}
      </Text>
      <Numerical
        size="xl"
        color="palette.orange.regular.500"
        fontWeight="bold"
        data-cy="price-changed-modal-new-price"
      >
        {getFormattedPrice(price_updated)}
      </Numerical>
      <Text size="md" color="text.tertiary.opaque" align="center">
        {translator.t("!results.modal.confirm_price.message.explanation", {
          operator_name
        })}
      </Text>
    </Stack>
  );

  const onAction = (button_type: ErrorModalButtonType) => {
    if (button_type === "cta") {
      onAccept && onAccept();
      return addToCartAcceptNewPrice(id, is_async_cart);
    }
    const url_redirect = getRedirectUrl(urlBuilder, search);
    addToCartRefuseNewPrice(id, is_async_cart, url_redirect);
  };

  return (
    <AlertDialog
      title={false}
      message={message}
      actionHandler={onAction}
      cta_button={{
        label: translator.t("!results.modal.confirm_price.button.book"),
        props: {
          "data-cy": "price-changed-modal-confirm"
        }
      }}
      secondary_button={{
        label: translator.t("!results.modal.confirm_price.button.back"),
        props: {
          "data-cy": "price-changed-modal-reject"
        }
      }}
    />
  );
};

const mapStateToProps = () => {
  return (state: ResultsReduxState, ownProps: OwnProps) => {
    const operators = getSearchedLegOperators(state, ownProps.id);
    const price_selected = getTripDisplayPrice(state, ownProps.id);
    const displayed_price_config = getDisplayedPriceConfig(state);

    return {
      currency: state.config.currency,
      operator_name: operators[0]?.name || "",
      price_selected,
      search: state.search,
      displayed_price_config
    };
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    { addToCartAcceptNewPrice, addToCartRefuseNewPrice },
    dispatch
  );

const connector = connect(mapStateToProps, mapDispatchToProps);
type ConnectorProps = ConnectedProps<typeof connector> & OwnProps;

export const PriceChanged = connector(PriceChangedComponent);
